import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Users } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getUserData, usersAPI } from "@/lib/api";
import { translateDomainName } from "@/lib/strength-i18n";
import { STRENGTHS_EXPLORER_DOMAIN_COLORS } from "@/utils/constants/strengths";
import { useToast } from "@/hooks/use-toast";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

interface DirectoryUser {
  id: string;
  name: string;
  profileImage?: string | null;
  profileImageDisplayUrl?: string | null;
  topDomains?: { domain: string; count: number }[];
}

interface DirectoryResponse {
  data: DirectoryUser[];
  count: number;
  total: number;
  totalPages: number;
  currentPage: number;
}

const PAGE_SIZE = 12;

/** Embeddable user directory grid, shared by the standalone /directory page and the dashboard tab. */
export default function UserDirectoryList() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user] = useState(() => getUserData());
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const loadDirectory = useCallback(async (page: number, append = false) => {
    append ? setIsLoadingMore(true) : setIsLoading(true);

    try {
      const response = await usersAPI.getDirectory(page, PAGE_SIZE) as DirectoryResponse;

      setUsers((current) => append ? [...current, ...(response.data || [])] : (response.data || []));
      setCurrentPage(response.currentPage || page);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
    } catch (error: any) {
      toast({
        title: t("userDirectory.loadFailed"),
        description: getLocalizedErrorMessage(error, "errors.fetchUsersFailed"),
        variant: "destructive",
      });
    } finally {
      append ? setIsLoadingMore(false) : setIsLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }

    loadDirectory(1);
  }, [loadDirectory, navigate, user]);

  const hasMore = currentPage < totalPages;
  const categoryLabel = user?.category === "graduate"
    ? t("admin.categoryGraduate")
    : t("admin.categoryStudent");

  return (
    <div className="min-w-0 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-2xl font-bold tracking-normal sm:text-3xl">
              {t("userDirectory.title")}
            </h2>
            <Badge variant="secondary">{categoryLabel}</Badge>
          </div>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            {t("userDirectory.subtitle")}
          </p>
        </div>

        <div className="flex w-fit items-center gap-2 rounded-md border bg-card px-4 py-3 text-sm text-muted-foreground sm:shrink-0">
          <Users className="h-4 w-4 text-primary" />
          <span>{t("userDirectory.total", { count: total })}</span>
        </div>
      </div>

      {isLoading ? (
        <Card className="flex items-center justify-center gap-3 p-8 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          {t("userDirectory.loading")}
        </Card>
      ) : users.length === 0 ? (
        <Card className="p-8 text-center text-muted-foreground">
          {t("userDirectory.empty")}
        </Card>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {users.map((directoryUser) => (
              <Link
                key={directoryUser.id}
                to={`/directory/${directoryUser.id}/profile`}
                className="block rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                <Card className="h-full p-5 transition-colors hover:border-primary/40">
                  <div className="flex min-w-0 items-center gap-4">
                    <Avatar className="h-14 w-14">
                      <AvatarImage
                        src={directoryUser.profileImageDisplayUrl || directoryUser.profileImage || undefined}
                        alt={directoryUser.name}
                      />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {directoryUser.name?.charAt(0).toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <h3 className="break-words text-lg font-semibold leading-tight">
                        {directoryUser.name}
                      </h3>
                    </div>
                  </div>

                  {directoryUser.topDomains && directoryUser.topDomains.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border/60 pt-3">
                      {directoryUser.topDomains.map(({ domain, count }) => {
                        const color = STRENGTHS_EXPLORER_DOMAIN_COLORS[domain];
                        return (
                          <Badge
                            key={domain}
                            className="border text-xs"
                            style={{ backgroundColor: `${color}15`, borderColor: `${color}40`, color }}
                          >
                            {translateDomainName(domain, t, i18n.language)} ({count})
                          </Badge>
                        );
                      })}
                    </div>
                  )}
                </Card>
              </Link>
            ))}
          </div>

          {hasMore && (
            <div className="flex justify-center pt-2">
              <Button
                variant="outline"
                onClick={() => loadDirectory(currentPage + 1, true)}
                disabled={isLoadingMore}
              >
                {isLoadingMore && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("userDirectory.loadMore")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
